import {
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { TimezoneService } from 'src/app/shared/services/timezone.service';

import { businessHoursValidator } from './business-hours.validator';

@Component({
  selector: 'app-business-hours',
  templateUrl: './business-hours.component.html',
  styleUrls: ['./business-hours.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BusinessHoursComponent {
  @Input() businessHoursCreateForm: FormGroup;
  @Output() enableSave: EventEmitter<any> = new EventEmitter();

  timezoneItems: any;
  daysOfWeek: string[];
  daysOfWeekItems: Choice[];
  timezoneLabel = this.basicService.translate('preferences/timezone');
  weekDayForm = this.formBuilder.group(
    {
      id: [0],
      start: this.formBuilder.group({ h: [8], m: [0] }),
      end: this.formBuilder.group({ h: [18], m: [0] }),
    },
    { validators: businessHoursValidator }
  );

  exceptionForm = this.formBuilder.group(
    {
      date: [moment()],
      start: this.formBuilder.group({ h: [8], m: [0] }),
      end: this.formBuilder.group({ h: [18], m: [0] }),
    },
    { validators: businessHoursValidator }
  );
  dayLabel = this.basicService.translate('account/business-hours/week/edit/day');
  startHours = this.formatTime({ h: 8, m: 0 });
  endHours = this.formatTime({ h: 18, m: 0 });
  closed = false;

  get timezoneControl() {
    return this.businessHoursCreateForm?.get('timezone') as FormControl;
  }

  get weekControl() {
    return this.businessHoursCreateForm?.get('week') as FormArray;
  }

  get exceptionsControl() {
    return this.businessHoursCreateForm?.get('exceptions') as FormArray;
  }

  @ViewChild('modifyBusinessHoursTemplateRef') modifyBusinessHoursTemplateRef: TemplateRef<any>;
  @ViewChild('modifyExceptionTemplateRef') modifyExceptionTemplateRef: TemplateRef<any>;

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private timezoneService: TimezoneService,
    @Inject('BusinessHoursFactory') businessHoursFactory: any
  ) {
    this.daysOfWeek = businessHoursFactory.daysOfWeek();
    this.daysOfWeekItems = this.daysOfWeek.map((day, index) => ({
      id: index,
      label: this.basicService.translate(day),
    }));
    const allTimezones = this.alcmeonContextService.self.all_timezones;
    this.timezoneItems = this.timezoneService.buildTimezoneDropdownItems(allTimezones);
  }

  addWeekDay() {
    this.weekControl.push(
      this.formBuilder.group({
        id: [0, Validators.required],
        start: this.formBuilder.group({ h: [8, Validators.required], m: [0, Validators.required] }),
        end: this.formBuilder.group({ h: [18, Validators.required], m: [0, Validators.required] }),
      })
    );
    this.enableSave.emit();
  }

  deleteWeekDay(index) {
    this.weekControl.removeAt(index);
    this.enableSave.emit();
  }

  editWeekDay(index: number) {
    const dayFound = (this.weekControl.value || [])[index];
    if (dayFound) {
      this.weekDayForm.patchValue(dayFound);

      const { start, end } = this.weekDayForm.value;
      this.startHours = this.formatTime(start);
      this.endHours = this.formatTime(end);
    }

    this.weekDayForm.patchValue(this.weekControl[index]);
    const { start, end } = this.weekDayForm.value;
    this.startHours = this.formatTime(start);
    this.endHours = this.formatTime(end);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'medium-size',
      data: {
        title: this.basicService.translate('account/business-hours/week/edit'),
        disable: () => this.weekDayForm.invalid,
        templateRef: this.modifyBusinessHoursTemplateRef,
        confirmBtnText: this.basicService.translate('ok'),
        cancelBtnText: this.basicService.translate('cancel'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.weekControl.at(index).patchValue(this.weekDayForm.value);
      }
    });
  }

  addException() {
    this.exceptionsControl.push(
      this.formBuilder.group({
        date: [moment().format('YYYY-MM-DD'), Validators.required],
        start: this.formBuilder.group({ h: [8, Validators.required], m: [0, Validators.required] }),
        end: this.formBuilder.group({ h: [18, Validators.required], m: [0, Validators.required] }),
      })
    );
    this.enableSave.emit();
  }

  deleteException(index) {
    this.exceptionsControl.removeAt(index);
    this.enableSave.emit();
  }

  editException(index) {
    const expectionFound = (this.exceptionsControl.value || [])[index];
    if (expectionFound) {
      this.exceptionForm.patchValue(expectionFound);

      const { start, end } = this.exceptionForm.value;
      this.startHours = this.formatTime(start);
      this.endHours = this.formatTime(end);
      this.closed = this.startHours === '00:00' && this.endHours === '00:00';
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'medium-size',
      data: {
        title: this.basicService.translate('account/business-hours/exception/edit'),
        disable: () => this.exceptionForm.invalid,
        templateRef: this.modifyExceptionTemplateRef,
        confirmBtnText: this.basicService.translate('ok'),
        cancelBtnText: this.basicService.translate('cancel'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const exception = this.exceptionForm.value;
        if (this.closed) {
          exception.start = { h: 0, m: 0 };
          exception.end = { h: 0, m: 0 };
        }
        this.exceptionsControl.at(index).patchValue(this.exceptionForm.value);
        this.closed = false;
      }
    });
  }

  private padLeading(i) {
    return `000${i}`.slice(-2);
  }

  formatTime(t) {
    return `${this.padLeading(t.h)}:${this.padLeading(t.m)}`;
  }

  updateHours(value: string, form: FormGroup, controlName: string) {
    const [hours, minutes] = value.split(':');
    form
      .get(controlName)
      .get('h')
      .setValue(+hours);
    form
      .get(controlName)
      .get('m')
      .setValue(+minutes);
  }
}
